import { Component, inject, computed, effect, PLATFORM_ID, signal, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { RouterOutlet, RouterLink, RouterLinkActive, Router } from '@angular/router';
import { LucideAngularModule, ShoppingBag, MessageCircle, Briefcase, Calendar, LogOut, Globe, Sun, Moon, Check, Wallet, Mail, House, Lock } from 'lucide-angular';
import { NavButtonComponent } from '../../shared/ui/nav-button/nav-button.component';
import { CashSessionBarComponent } from '../../shared/ui/cash-session-bar/cash-session-bar.component';
import { CallOverlayComponent } from '../../shared/ui/call-overlay/call-overlay.component';
import { PwaInstallBannerComponent } from '../../shared/ui/pwa-install-banner/pwa-install-banner.component';
import { UserService } from '../../core/services/user.service';
import { TranslationService, LanguageOption } from '../../core/services/translation.service';
import { AuthService } from '../../core/services/auth.service';
import { CashRegisterService } from '../../core/services/cash-register.service';
import { CartService } from '../../core/services/cart.service';
import { ChatService } from '../../core/services/chat.service';
import { ModePosEntreprise } from '../../core/services/mode-pos-entreprise';
import { themeInitial } from '../../core/theme';
import { VerrouPoste } from '../../core/services/verrou-poste';
import { EcranVerrouComponent } from '../../shared/ui/ecran-verrou/ecran-verrou.component';
import { FeuilleInstallationIosComponent } from '../../shared/ui/feuille-installation-ios/feuille-installation-ios.component';
import { MiseAJourApplication, PwaInstallService } from '@stockflow/offline-core';
import { BanniereMiseAJourComponent, HAUTEUR_BANNIERE_MAJ } from '../../shared/ui/banniere-mise-a-jour/banniere-mise-a-jour.component';
import { habillageMenuCompte, itemsMenuCompte } from './menu-compte';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    NavButtonComponent,
    CashSessionBarComponent,
    CallOverlayComponent,
    PwaInstallBannerComponent,
    BanniereMiseAJourComponent,
    FeuilleInstallationIosComponent,
    EcranVerrouComponent,
    LucideAngularModule
  ],
  templateUrl: './main-layout.component.html'
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  userService = inject(UserService);
  translationService = inject(TranslationService);
  authService = inject(AuthService);
  cashRegisterService = inject(CashRegisterService);
  cartService = inject(CartService);
  chatService = inject(ChatService);
  verrou = inject(VerrouPoste);
  /** Mode du POS mobile du plan : SALES garde le menu de vente, RH montre le menu « sans vente ». */
  modePos = inject(ModePosEntreprise);
  router = inject(Router);
  document = inject(DOCUMENT);
  platformId = inject(PLATFORM_ID);

  // Load user profile on layout init
  private profileLoaded = false;

  // Expose translation signal to template
  t = this.translationService.t;
  // Use local signal for dark mode
  isDarkMode = signal(false);

  // Fix: I will use the local signal for dark mode as before.
  showLanguageMenu = signal(false);

  readonly languages = this.translationService.availableLanguages;

  currentLanguage = computed(() => {
    const code = this.translationService.currentLang();
    return this.languages.find(l => l.code === code) || this.languages[0];
  });

  readonly ShoppingBag = ShoppingBag;
  readonly MessageCircle = MessageCircle;
  readonly Briefcase = Briefcase;
  readonly Calendar = Calendar;
  readonly LogOut = LogOut;
  readonly Globe = Globe;
  readonly Sun = Sun;
  readonly Moon = Moon;
  readonly Check = Check;
  readonly Wallet = Wallet;
  readonly Mail = Mail;
  readonly House = House;
  readonly Lock = Lock;

  /** Menu contextuel avatar (Taches/Configuration/Code NIP/Se deconnecter). */
  avatarMenuOpen = signal(false);

  private readonly pwa = inject(PwaInstallService);
  private readonly miseAJour = inject(MiseAJourApplication);
  /** La marche a suivre d'installation sur iPhone / iPad. */
  readonly feuilleInstallation = signal(false);

  /** « Installer l'application » : l'invite du navigateur, ou la marche a suivre sur iOS. */
  async installer() {
    this.avatarMenuOpen.set(false);
    if ((await this.pwa.installer()) === 'manuelle') this.feuilleInstallation.set(true);
  }

  /** Lignes du menu du compte, dans l'ordre du mode courant. */
  readonly itemsMenu = computed(() => itemsMenuCompte(this.modePos.estVente(), this.t(), this.pwa.proposable()));
  /** Habillage du menu du compte : celui de la vente, ou celui de la maquette « sans vente ». */
  readonly habillageMenu = computed(() => habillageMenuCompte(this.modePos.estVente()));

  readonly hasActiveSession = computed(() => this.modePos.estVente() && !!this.cashRegisterService.activeSession());

  /** Distance du haut de l'ecran sous laquelle commence le contenu : en-tete, et barre de caisse quand elle est la. */
  readonly enteteHaut = computed(() => (this.hasActiveSession() ? 86 : 56));
  /** La bannière « nouvelle version » est un bandeau : le contenu descend d'autant pour ne rien recouvrir. */
  readonly contenuHaut = computed(() =>
    this.miseAJour.etat() === 'disponible' ? this.enteteHaut() + HAUTEUR_BANNIERE_MAJ : null,
  );

  /** Message bref apres un appui sur « Verrouiller le poste » qui n'a pas verrouille tout de suite. */
  messageVerrou = signal<string | null>(null);
  private minuterieMessage: ReturnType<typeof setTimeout> | null = null;

  /** Verrouille le poste. Sans NIP : renvoie vers sa creation ; avec une vente en cours : attend et le dit. */
  async verrouiller() {
    this.avatarMenuOpen.set(false);
    const resultat = await this.verrou.verrouiller();
    if (resultat === 'sans-nip') {
      this.annoncer(this.t().verrou.no_pin);
      void this.router.navigate(['/settings'], { queryParams: { view: 'pin' } });
    } else if (resultat === 'en-attente') {
      this.annoncer(this.t().verrou.pending);
    }
  }

  private annoncer(texte: string) {
    this.messageVerrou.set(texte);
    if (this.minuterieMessage !== null) clearTimeout(this.minuterieMessage);
    this.minuterieMessage = setTimeout(() => this.messageVerrou.set(null), 5000);
  }

  ngOnDestroy() {
    if (this.minuterieMessage !== null) clearTimeout(this.minuterieMessage);
  }

  ngOnInit() {
    // Le verrou suit l'inactivite et le raccourci tant que le layout est la ; il relit aussi la marque
    // laissee par un verrou d'avant un rechargement.
    this.verrou.demarrer();
    // Load active cash session on layout init — seulement quand le plan vend :
    // sans vente il n'y a pas de caisse, donc pas d'appel.
    if (isPlatformBrowser(this.platformId) && this.modePos.estVente()) {
      this.cashRegisterService.getActiveSession().catch(() => {});
    }
  }

  constructor() {
    // Determine initial theme (could be from localStorage)
    if (isPlatformBrowser(this.platformId)) {
      this.isDarkMode.set(themeInitial() === 'dark');
    }

    // Load user profile
    if (isPlatformBrowser(this.platformId) && !this.profileLoaded) {
      this.profileLoaded = true;
      this.userService.loadProfile();
    }

    // Apply theme effect
    effect(() => {
      if (isPlatformBrowser(this.platformId)) {
        if (this.isDarkMode()) {
          this.document.documentElement.classList.add('dark');
          localStorage.setItem('theme', 'dark');
        } else {
          this.document.documentElement.classList.remove('dark');
          localStorage.setItem('theme', 'light');
        }
      }
    });
  }

  // Helper to match template usage
  darkMode() {
    return this.isDarkMode();
  }

  toggleTheme() {
    this.isDarkMode.update((v) => !v);
  }

  toggleLanguageMenu() {
    this.showLanguageMenu.update((v) => !v);
  }

  toggleAvatarMenu() {
    this.avatarMenuOpen.update((v) => !v);
  }

  goToSettingsView(view: 'todos' | 'config' | 'pin') {
    this.avatarMenuOpen.set(false);
    this.router.navigate(['/settings'], { queryParams: { view } });
  }

  setLanguage(lang: LanguageOption) {
    this.translationService.setLanguage(lang.code);
    this.showLanguageMenu.set(false);
  }

  /**
   * Vrai quand la photo n'a pas pu etre affichee.
   *
   * L'URL peut exister et l'image rester introuvable : objet S3 supprime,
   * reseau coupe en boutique, lien expire. Sans ce repli, l'en-tete montre
   * l'icone d'image cassee du navigateur — pire que pas de photo du tout.
   */
  photoIndisponible = signal(false);

  /** Initiales du nom, utilisees a defaut de photo. */
  initiales(nom: string): string {
    return (nom || '')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((m) => m[0].toUpperCase())
      .join('');
  }

  logout() {
    this.authService.logout('bouton Deconnexion (en-tete)');
  }
}
